import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/css';
import { Editor, Descendant, Transforms, Text } from 'slate';
import { Slate, Editable } from 'slate-react';
import { useCustomUpload } from 'hooks/business/useUploadResource';
import FooterImagePlugin from './components/FooterImagePlugin';
import Leaf from './renders/Leaf';
import { CustomSlateNodeType, ImageInfo, LySlatePlugin, PluginParseUtils } from './types';
import lySlatePluginParser from './utils/lySlatePluginParser';
import { ParagraphElement } from './slateTypes';
import { Button } from 'antd';
import ErrorBoundary from 'components/ErrorBoundary';

const styles = {
  container: css`
    width: 100%;
    border: 1px solid #d3d7e0;
    background-color: #fff;
    .ant-form-item-has-error & {
      border-color: red;
    }
  `,
  editor: css`
    padding: 12px;
    height: 128px;
    overflow-y: auto;
    * {
      word-break: break-all;
    }
  `,
  imageContainer: css`
    padding: 8px 12px;
    height: 92px;
    border-top: 1px solid #d3d7e0;
  `,
  tailText: css`
    padding: 0 12px;
    font-size: 12px;
    color: #84868c;
  `,
};

function combineParagraph(segments: Descendant[]) {
  return segments.reduce<Descendant[]>((res, currentSegment) => {
    if (currentSegment.type !== CustomSlateNodeType.PARAGRAPH || res.length === 0) {
      res.push(currentSegment);

      return res;
    }

    const lastSegment = res[res.length - 1];

    if (lastSegment.type !== CustomSlateNodeType.PARAGRAPH) {
      res.push(currentSegment);

      return res;
    }

    res[res.length - 1] = {
      ...lastSegment,
      children: [...lastSegment.children, ...currentSegment.children],
    };

    return res;
  }, []);
}

function combineParagraphText(elements: ParagraphElement['children']) {
  return elements.reduce<ParagraphElement['children']>((res, currentElement) => {
    if (!Text.isText(currentElement) || res.length === 0) {
      res.push(currentElement);

      return res;
    }

    const lastElement = res[res.length - 1];

    if (!Text.isText(lastElement)) {
      res.push(currentElement);

      return res;
    }

    res[res.length - 1] = {
      ...lastElement,
      text: `${lastElement.text}\n${currentElement.text}`,
    };

    return res;
  }, []);
}

function getCombinedSegments(segments: Descendant[]) {
  const paragraphCombinedSegments = combineParagraph(segments);

  const textCombinedSegments = paragraphCombinedSegments.map((segment) => {
    if (segment.type !== CustomSlateNodeType.PARAGRAPH) {
      return segment;
    }

    return {
      ...segment,
      children: combineParagraphText(segment.children || []),
    };
  });

  return textCombinedSegments;
}

export interface TemplateEditorProps {
  value: Descendant[];
  // 如果shouldSerializing为false,则吐出的数据为Node[];否则吐出serializr()函数格式化后的数据
  onChange?: (value: Descendant[]) => void;
  // 要使用的插件数组
  lySlatePlugins?: LySlatePlugin[];
  // 工具条中要渲染的元素
  toolbarChildren?: ReactNode | false;
  // 是否需要使用底部的图片插件
  useFooterPlugin?: boolean;
  storeId?: string;
  placeholder?: string;
  // 是否为只读模式
  readOnly?: boolean;
  // 文字和图片插件之间插入的文字,不填或为空则不展示
  textTail?: string;
  children?: ReactNode;
  onBlur?: Parameters<typeof Editable>[0]['onBlur'];
}

const TemplateEditor = (props: TemplateEditorProps) => {
  const {
    value,
    onChange,
    placeholder,
    readOnly,
    lySlatePlugins = [],
    toolbarChildren = false,
    useFooterPlugin = false,
    storeId,
    textTail,
    children = null,
    onBlur,
  } = props;

  const [{ editor, renderElement }] = useState<PluginParseUtils>(
    lySlatePluginParser(lySlatePlugins),
  );

  const customUpload = useCustomUpload();

  const { valueWithoutImages, images } = useMemo(() => {
    const initialValue = {
      valueWithoutImages: [] as Descendant[],
      images: [] as ImageInfo[],
    };

    return value.reduce((res, item) => {
      if (item.type === CustomSlateNodeType.IMAGE) {
        if (useFooterPlugin) {
          res.images = item.images;
        }
      } else {
        res.valueWithoutImages.push(item);
      }

      return res;
    }, initialValue);
  }, [value, useFooterPlugin]);

  const handleChange = useCallback(
    (newValue: Descendant[]) => {
      if (!readOnly && value !== newValue) {
        onChange?.(getCombinedSegments(newValue));
      }
    },
    [onChange, readOnly, value],
  );

  const renderLeaf = useCallback((props) => {
    return <Leaf {...props} />;
  }, []);

  useEffect(() => {
    // 因为第一次传值进来的时候,不会自动normalize,所以对第一个有效value手动normalize
    Editor.normalize(editor, { force: true });
  }, [editor]);

  const content = (
    <Slate
      editor={editor}
      value={valueWithoutImages}
      onChange={useCallback(
        (nextValue) => {
          handleChange(
            images.length === 0
              ? nextValue
              : [
                  ...nextValue,
                  {
                    type: CustomSlateNodeType.IMAGE,
                    images,
                    children: [],
                  },
                ],
          );
        },
        [images, handleChange],
      )}
    >
      <div className={styles.container}>
        {!readOnly && toolbarChildren}
        <Editable
          placeholder={placeholder}
          readOnly={readOnly}
          className={styles.editor}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          scrollSelectionIntoView={() => {}}
          onBeforeInput={() => {
            setTimeout(() => {
              if (editor.selection) {
                editor.apply({
                  type: 'set_selection',
                  properties: null,
                  newProperties: {
                    anchor: editor.selection.anchor,
                    focus: editor.selection.focus,
                  },
                });
              }
            });
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              Transforms.insertText(editor, '\n');
            }
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              Transforms.move(editor, { distance: 1, unit: 'offset', reverse: true });
            }
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              Transforms.move(editor, { distance: 1, unit: 'offset' });
            }
            // if (event.key === 'Backspace') {
            //   event.preventDefault();
            //   Editor.point()
            //   console.log(Editor.before(editor));
            //   Transforms.delete(editor, {
            //     distance: 1,
            //     unit: 'character',
            //     reverse: true,
            //   });
            // }
          }}
          onBlur={onBlur}
        />
        {textTail && <div className={styles.tailText}>{textTail}</div>}
        {useFooterPlugin && storeId ? (
          <FooterImagePlugin
            key={images.reduce((dest, cur) => `${dest},${cur.imageId}`, '')}
            fileSource={images}
            onImagesChange={(images) => {
              handleChange(
                images.length === 0
                  ? valueWithoutImages
                  : [
                      ...valueWithoutImages,
                      {
                        type: CustomSlateNodeType.IMAGE,
                        images,
                        children: [],
                      },
                    ],
              );
            }}
            disabled={readOnly}
            maxCount={3}
            customRequest={(options) =>
              customUpload(options, {
                storeId,
                fileType: 'IMAGE',
                reqSource: 'TEMPLATE_EDITOR',
              })
            }
          />
        ) : null}
        {children}
      </div>
    </Slate>
  );

  return (
    <ErrorBoundary
      componentName="TemplateEditor"
      fallback={(_, reset) => (
        <div>
          <Button type="link" onClick={reset}>
            未知错误，点此恢复
          </Button>
        </div>
      )}
    >
      {content}
    </ErrorBoundary>
  );
};

export default TemplateEditor;
